<template>
    <section class="registration">
        <transition-group
            name="custom-classes-transition"
            enter-active-class="animated fadeInRight"
            leave-active-class="animated fadeOutRight"
            mode="out-in"
        >
            <div class="registration__step1" v-if="showNotFound" key="step-1" v-cloak>
                <div class="registration__confirmation container" v-cloak>
                    <div class="messaging -error -light -spaced" v-if="errorMessage">
                        <h3 class="messaging__text">There is a problem with your registration</h3>
                        <p class="messaging__text" v-html="errorMessage"></p>
                    </div>
                </div>
            </div>

            <div class="registration__step1" v-if="showSessionsTab" key="step-1">
                <div v-if="errorMessage">
                    <p class="messaging -error -light -spaced" v-text="errorMessage"></p>
                </div>
                <header class="registration__header">
                    <div class="container">
                        <strong class="caps">Registration for:</strong>
                        <h2 class="registration__eventTitle" v-text="event.title"></h2>
                        <ul class="registration__eventDetails grid -col3">
                            <li class="grid__cell"><strong class="caps">When:</strong><span
                                class="registration__details"
                                v-text="displayDate"></span></p></li>
                            <li class="grid__cell"><strong class="caps">Where:</strong><span
                                class="registration__details"
                                v-html="displayLocation"></span></li>
                            <li class="grid__cell" v-if="displayHost"><strong class="caps">Host/Primary
                                Sponsor: </strong><span
                                class="registration__details" v-text="displayHost"></span></li>
                        </ul>
                    </div>
                </header>
                <form class="registration__form container" @submit.prevent="continueToStep2()" data-vv-scope="step-1">
                    <ul class="tabs__list -steps" id="sessionTabs">
                        <li class="tabs__tab -active"><span class="tabs__button"><span
                            class="tabs__buttonText">Choose<br>Sessions</span></span></li>
                        <li class="tabs__tab"><span class="tabs__button"><span
                            class="tabs__buttonText">Verify<br>Registration</span></span></li>
                    </ul>
                    <span class="caps">Step 1 of 2</span>
                    <h3 class="registration__heading">Sessions</h3>
                    <p>
                        Please choose the sessions you plan to attend.
                    </p>
                    <bp-tabs :tabs="agendaTabs" class="-agenda -condensed" @selected-tab="updateAgendaTab"
                             ref="agendaTabs">
                        <template v-for="panelData in agendaPanels" :slot="panelData.slot" :panelData="panelData">
                            <div class="responsiveTable">
                                <table :class="agendaColumns(panelData.sessions)">
                                    <tr class="agenda__trackNames">
                                        <th>Time</th>
                                        <th v-for="track in parseTracks(panelData.sessions)"><span
                                            v-text="track"></span></th>
                                    </tr>
                                    <template v-for="(columns,hour) in panelData.sessions" :hour="hour"
                                              :columns="columns">
                                        <tr v-if="hasTracks(columns)">
                                            <td v-text="formatHour(panelData.date, hour)"></td>
                                            <td v-for="track in parseTracks(panelData.sessions)">
                                                <template v-for="session in filterTrackedSessions(columns, track)">
                                                    <div class="checkboxes">
                                                        <input v-if="showCheckbox(session)" class="checkbox__input"
                                                               type="checkbox"
                                                               :id="checkboxId(session)" v-model="session.isSelected">
                                                        <label class="checkbox__label" :for="checkboxId(session)">
                                                            <span v-text="session.sessionTitle"></span><br>
                                                            <em v-text="formatSessionTime(session)"></em><br>
                                                        </label>
                                                    </div>
                                                    <div v-if="showAdditionalInfo(session)" class="text">
                                                        <label>Additional Information</label>
                                                        <input class="text__input" type="text"
                                                               v-model="session.additionalInformation"
                                                               placeholder="Dietary restrictions, special requirements, etc">
                                                    </div>
                                                </template>
                                            </td>
                                        </tr>
                                        <tr v-if="spannedSessions(columns)">
                                            <td v-text="formatHour(panelData.date, hour)"></td>
                                            <td colspan="3">
                                                <template v-for="session in spannedSessions(columns)">
                                                    <div class="checkboxes">
                                                        <input class="checkbox__input" v-if="showCheckbox(session)"
                                                               type="checkbox"
                                                               :id="checkboxId(session)" v-model="session.isSelected">
                                                        <label class="checkbox__label" :for="checkboxId(session)">
                                                            <span v-text="session.sessionTitle"></span><br>
                                                            <em v-text="formatSessionTime(session)"></em>
                                                        </label>
                                                    </div>
                                                    <div v-if="showAdditionalInfo(session)" class="text">
                                                        <label>Additional Information</label>
                                                        <input class="text__input" type="text"
                                                               v-model="session.additionalInformation"
                                                               placeholder="Dietary restrictions, special requirements, etc">
                                                    </div>
                                                </template>
                                            </td>
                                        </tr>
                                    </template>
                                </table>
                            </div>
                        </template>
                    </bp-tabs>

                    <div class="action -spaced">
                        <button type="button" name="continue" @click="continueToStep2"
                                v-text="continueButtonText"></button>
                        <div v-if="errorMessage">
                            <p class="error" v-html="errorMessage"></p>
                        </div>
                    </div>
                </form>
            </div>
            <div class="registration__step2" v-if="scope == 'step-2'" key="step-2">
                <header class="registration__header">
                    <div class="container">
                        <strong class="caps">Registration for:</strong>
                        <h2 class="registration__eventTitle" v-text="event.title"></h2>
                        <ul class="registration__eventDetails grid -col3">
                            <li class="grid__cell"><strong class="caps">When:</strong><span
                                class="registration__details"
                                v-text="displayDate"></span></p></li>
                            <li class="grid__cell"><strong class="caps">Where:</strong><span
                                class="registration__details"
                                v-html="displayLocation"></span></li>
                            <li class="grid__cell" v-if="displayHost"><strong class="caps">Host/Primary
                                Sponsor: </strong><span
                                class="registration__details" v-text="displayHost"></span></li>
                        </ul>
                    </div>
                </header>
                <form class="registration__form registration__info container" @submit.prevent="completeRegistration()">
                    <ul class="tabs__list -steps" id="sessionTabs2">
                        <li class="tabs__tab" v-if="hasSessions"><span class="tabs__button" @click="backToStep1"><span
                            class="tabs__buttonText">Choose<br>Sessions</span></span></li>
                        <li class="tabs__tab -active"><span class="tabs__button"><span
                            class="tabs__buttonText">Verify<br>Registration</span></span>
                        </li>
                    </ul>
                    <span v-if="hasSessions" class="caps">Step 2 of 2</span>
                    <h3 class="registration__heading">Member Profile Information</h3>
                    <p>
                        The information below is part of your membership profile. Please verify that it is correct and
                        then click
                        Complete My Registration.
                        Any information you update here will automatically update your membership profile.
                        </span>
                    </p>
                    <fieldset class="grid -col3">
                        <div class="text grid__cell">
                            <label>First Name <span class="required">*</span></label>
                            <input
                                data-vv-as="First Name"
                                aria-label="First Name"
                                title="First Name"
                                v-model="user.firstName"
                                type="text"
                                name="firstName"
                                placeholder=""
                            />
                        </div>

                        <div class="text grid__cell">
                            <label>Last Name <span class="required">*</span></label>
                            <input
                                data-vv-as="Last Name"
                                aria-label="Last Name"
                                title="Last Name"
                                v-model="user.lastName"
                                type="text"
                                name="lastName"
                                placeholder=""
                            />
                        </div>

                        <div class="text grid__cell">
                            <label>Nickname</label>
                            <input
                                data-vv-as="Nickname"
                                aria-label="Nickname"
                                title="Nickname"
                                v-model="user.nickname"
                                type="text"
                                name="nickname"
                                placeholder=""
                            />
                        </div>

                        <div class="text grid__cell">
                            <label>Firm</label>
                            <input disabled
                                   data-vv-as="Firm"
                                   aria-label="Firm"
                                   title="Firm"
                                   v-model="user.firm"
                                   type="text"
                                   name="firm"
                                   placeholder=""
                            />
                        </div>

                        <div class="text grid__cell">
                            <label>Title/Position <span class="required">*</span></label>
                            <input
                                data-vv-as="Title"
                                aria-label="Title"
                                title="Title"
                                v-model="user.jobTitle"
                                type="text"
                                name="jobTitle"
                                placeholder=""
                            />
                        </div>

                        <!--<div class="text grid__cell">
                          <label>Email <span class="required">*</span></label>
                           <input
                          data-vv-as="Email"
                          aria-label="Email"
                          v-model="user.email"
                          type="text"
                          name="email"
                          />
                                        <div class="inputGroup -tagged -reverse">
                                            <input type="text" name="email" v-model="emailName">
                                            <span class="inputGroup__tag" v-text="emailDomain"></span>
                                        </div>
                                    </div> -->

                        <div class="text grid__cell">
                            <label>Phone <span class="required">*</span></label>
                            <input
                                data-vv-as="Phone"
                                aria-label="Phone"
                                title="Phone"
                                v-model="user.phone"
                                type="text"
                                name="phone"
                                placeholder=""
                            />
                        </div>

                        <h3 class="registration__heading" v-if="event.displayVendorShareCheckbox">Connect With Cutter
                            Partner(s)</h3>
                        <span style="margin-bottom: 5px;" v-if="event.displayVendorShareCheckbox">By checking the box below, you agree to share your contact information with the sponsor(s) of this event.</span>
                        <div class="text grid__cell" v-if="event.displayVendorShareCheckbox">
                            <label>
                                <input
                                    class="checkbox"
                                    data-vv-as="Consent"
                                    aria-label="Consent"
                                    title="Consent"
                                    v-model="vendorShare"
                                    type="checkbox"
                                    name="vendorShare"
                                    value="1"
                                    :disabled="event.registrationId ? true : false"
                                />
                                Share my contact information
                            </label>
                        </div>
                    </fieldset>

                    <div class="actions">
                        <button :disabled="processing" type="submit" name="submit" v-text="finishButtonText"></button>
                        <loading-circle v-if="processing"></loading-circle>
                        <div v-if="errorMessage">
                            <p class="messaging -error" v-html="errorMessage"></p>
                        </div>
                    </div>
                </form>
            </div>
            <div class="registration__step3" v-if="scope == 'step-3'" key="step-3">
                <header class="registration__header">
                    <div class="container">
                        <h2 class="registration__eventTitle">Thank you, <span v-text="user.firstName"></span>!</h2>
                        <div v-if="successMessage">
                            <p class="messaging -success -light" v-text="successMessage"></p>
                        </div>
                    </div>
                </header>
                <div class="registration__form registration__confirmation container">
                    <div class="grid -thirdReverse">
                        <div class="grid__cell">
                            <h2 class="heading" v-text="event.title"></h2>
                        </div>
                        <div class="grid__cell">
                            <a v-bind:href="event.calendarUrl" class="button">Add Event to Calendar</a>
                        </div>
                    </div>
                    <ul class="registration__eventDetails grid -col3">
                        <li class="grid__cell">
                            <strong class="caps">When:</strong>
                            <p v-text="displayDate"></p>
                        </li>
                        <li class="grid__cell">
                            <strong class="caps">Where:</strong>
                            <p v-html="displayLocation"></p>
                        </li>
                        <li class="grid__cell" v-if="displayHost">
                            <strong class="caps">Host/Primary Sponsor: </strong>
                            <p v-text="displayHost"></p>
                        </li>
                    </ul>
                    <span class="caps">Basic Information:</span>
                    <ul class="confirmation__eventDetails grid -col3">
                        <li class="grid__cell"><label class="meta -dark">First Name</label> <span
                            v-text="user.firstName"></span>
                        </li>
                        <li class="grid__cell"><label class="meta -dark">Last Name</label> <span
                            v-text="user.lastName"></span></li>
                        <li class="grid__cell"><label class="meta -dark">Nickname</label> <span
                            v-text="user.nickname"></span></li>
                        <li class="grid__cell"><label class="meta -dark">Firm</label> <span v-text="user.firm"></span>
                        </li>
                        <li class="grid__cell"><label class="meta -dark">Title</label> <span
                            v-text="user.jobTitle"></span></li>
                        <li class="grid__cell"></li>
                        <li class="grid__cell"><label class="meta -dark">Email</label> <span v-text="user.email"></span>
                        </li>
                        <li class="grid__cell"><label class="meta -dark">Phone</label> <span v-text="user.phone"></span>
                        </li>
                    </ul>
                    <div v-if="selectedSessions">
                        <span class="caps">Sessions:</span>
                        <table class="agenda -confirmation">
                            <tr class="agenda__trackNames">
                                <th class="sr-only">Session Date</th>
                                <th class="sr-only">Session Time</th>
                                <th class="sr-only">Session Name</th>
                                <th class="sr-only">Session Category</th>
                                <th class="sr-only"></th>
                            </tr>
                            <template v-for="(day, date) in sessions">
                                <template v-for="hour in day">
                                    <template v-for="session in hour">
                                        <tr v-if="session.isSelected">
                                            <td v-text="formatDate(session.sessionDate)"></td>
                                            <td v-text="formatSessionTime(session)"></td>
                                            <td>
                                                <div v-text="session.sessionTitle"></div>
                                                <div v-text="session.additionalInformation"></div>
                                            </td>
                                            <td v-text="session.track"></td>
                                            <td><a class="registration__addToCalendar"
                                                   v-bind:href="session.calendarUrl">Add to Calendar</a>
                                            </td>
                                        </tr>
                                    </template>
                                </template>
                            </template>
                        </table>
                    </div>
                    <p><a href="/events" class="button">Back to Events</a></p>
                </div>
            </div>
        </transition-group>
    </section>
</template>

<script>
import axios from "axios";
import BpTabs from "./BpTabs";
import LoadingCircle from "./LoadingCircle";
import moment from "moment-timezone";
import VueScrollTo from "vue-scrollto";
import VueSanitize from "vue-sanitize";

export default {
    props: ["id"],
    components: {
        axios: axios,
        "bp-tabs": BpTabs,
        moment: moment,
        "loading-circle": LoadingCircle,
    },
    data() {
        return {
            emailName: null,
            emailDomain: null,
            error: null,
            event: {
                calendarUrl: null,
                city: null,
                endDate: null,
                host: null,
                location: null,
                startDate: null,
                title: null,
                venue: null,
                timezone: null,
                displayVendorShareCheckbox: null,
            },
            hash: null,
            processing: false,
            //   progressClass: false,
            sessions: [],
            scope: "step-1",
            success: null,
            user: {
                email: null,
                firstName: null,
                lastName: null,
                nickname: null,
                firm: null,
                jobTitle: null,
                phone: null,
                timezone: null,
            },
            agendaTabsViewed: [],
            vendorShare: null,
        };
    },
    computed: {
        adjustedStartDate() {
            let start = "";
            if (this.event.startDate) {
                if (this.event.location == "Virtual") {
                    start = moment.tz(
                        this.event.startDate.date,
                        this.user.timezone
                    );
                } else {
                    start = moment.tz(
                        this.event.startDate.date,
                        this.event.timezone
                    );
                }

                return start;
            }

            return null;
        },
        adjustedEndDate() {
            let end = "";
            if (this.event.endDate) {
                if (this.event.location == "Virtual") {
                    end = moment.tz(
                        this.event.endDate.date,
                        this.user.timezone
                    );
                } else {
                    end = moment.tz(
                        this.event.endDate.date,
                        this.event.timezone
                    );
                }

                return end;
            }

            return null;
        },
        displayDate() {
            //let start = moment(this.event.startDate.date);
            let start = this.adjustedStartDate;

            if (start) {
                let output = start.format("MMMM D");

                //let end =  moment(this.event.endDate.date);
                let end = this.adjustedEndDate;

                if (end) {
                    let c = "";
                    let format = "YYYY";

                    if (start.format("MMMM") != end.format("MMMM")) {
                        output = output + " - " + end.format("MMMM D"); // Format as 'September 30 - October 1 2020'
                    } else if (start.format("D") != end.format("D")) {
                        output = output + "-" + end.format("D"); // Format as 'September 16-18 2020'
                    } else {
                        c = ",";
                        format = "YYYY h:mmA z"; // Format as 'September 16, 2020 9:15am EST'
                    }
                    output = output + c + " " + start.format(format);
                } else {
                    output = output + ", " + start.format("YYYY h:mmA z"); // Format as 'September 16, 2020 9:15am EST'
                }

                return output;
            }

            return "";
        },
        sourceTimezone() {
            if (this.event.startDate) {
                return this.event.timezone;
            }

            return "America/New_York";
        },
        userTimezone() {
            if (this.user.timezone) {
                return this.user.timezone;
            }

            return "America/New_York";
        },
        displayLocation() {
            let venue = "";
            if (this.event.venue) {
                venue = venue.concat(this.event.venue).concat("<br/>"); // Do fancy formatting?
            }
            if (this.event.city) {
                venue = venue.concat(this.event.city);
            }
            if (venue.length == 0 && this.event.location) {
                venue = this.event.location;
            }
            return venue;
        },
        displayHost() {
            return this.event.host;
        },
        agendaTabs() {
            let keys = Object.keys(this.sessions);
            if (keys.length) {
                let i = "";
                let k = "";
                let response = {};
                for (i = 0; i < keys.length; i++) {
                    k = "tab" + (i + 1);
                    response[k] = this.parseDateString(keys[i]).format("dddd, MMM D");
                }

                return response;
                // returns { 'tab1': 'Friday, Sep 17', 'tab2': 'Saturday, Sep 18', ... }
            }

            return "";
        },
        agendaPanels() {
            let keys = Object.keys(this.sessions);
            if (keys.length) {
                let i = "";
                let k = "";
                let response = [];
                for (i = 0; i < keys.length; i++) {
                    response[i] = {
                        slot: "tab-panel-tab" + (i + 1),
                        date: this.parseDateString(keys[i]).format("YYYY-MM-DD"),
                        sessions: this.sessions[keys[i]],
                    };
                }

                return response;
                // returns { {slot:'tab-panel-tab1', date:2020-09-17,sessions:{...}}, {slot:'tab-panel-tab2',date:2020-09-18,sessions:{...}}, ... }
            }

            return "";
        },
        showNotFound() {
            return !this.hasEvent;
        },
        showSessionsTab() {
            if (!this.hasSessions) {
                return false;
            }

            return this.scope == "step-1";
        },
        selectedSessions() {
            let days = Object.keys(this.sessions);

            if (days.length == 0) {
                return "";
            }
            /* Formats an array of selected sessions with additional info appended */
            /* ['id' => 43, 'additional' => 'Require gluten free'], ['id' => 53], ['id' => 58]] */

            let times = "";
            let selectedSessions = [];
            let i = 0;
            let j = 0;
            let k = 0;
            let key = "";
            let time = "";
            let session = "";

            for (i = 0; i < days.length; i++) {
                key = days[i];

                times = Object.keys(this.sessions[key]);

                for (j = 0; j < times.length; j++) {
                    time = times[j];

                    for (k = 0; k < this.sessions[key][time].length; k++) {
                        session = this.sessions[key][time][k];

                        if (session.isSelected) {
                            if (session.additionalInformation) {
                                selectedSessions.push({
                                    id: session.id,
                                    sessionId: session.sessionId,
                                    additional: session.additionalInformation,
                                });
                            } else {
                                selectedSessions.push({
                                    id: session.id,
                                    sessionId: session.sessionId
                                });
                            }
                        }
                    }
                }
            }

            return selectedSessions;
        },
        hasEvent() {
            return this.event.title && this.event.startDate ? true : false;
        },
        hasSessions() {
            return Object.keys(this.sessions).length > 0;
        },
        successMessage() {
            if (this.success) {
                return this.success;
            }
        },
        errorMessage() {
            if (this.error) {
                /* if (this.error.includes('TimeZone')) {
                    return 'Your profile is missing required information. Please <a href="/profile">update it here.</a>';
                } */
                return this.error;
            }
        },
        completedAction() {
            if (this.event.registrationId) {
                return "updated";
            }
            return "submitted";
        },
        finishButtonText() {
            if (this.event.registrationId) {
                return "Update My Registration";
            }
            return "Complete My Registration";
        },
        continueButtonText() {
            let nextTab = this.nextAgendaTab;
            if (nextTab) {
                return 'Continue to ' + this.agendaTabs[nextTab];
            }

            return 'Continue to Next Step';
        },
        nextAgendaTab() {
            let tabs = Object.keys(this.agendaTabs);

            if (tabs) {
                let i = 0;
                for (i = 0; i < tabs.length; i++) {
                    if (-1 === this.agendaTabsViewed.indexOf(tabs[i])) {
                        return tabs[i];
                    }
                }
            }

            return false;
        }
    },
    methods: {
        parseDateString(value) {
            // expects a string date value
            if (value) {
                let m = moment(value);

                return m;
            }

            return "";
        },
        adjustDate(value) {
            // expects a moment date object
            /*if (this.event.location == "Virtual") {
                if (this.sourceTimezone != this.userTimezone) {
                    return value.clone().tz(this.userTimezone);
                }
            }*/

            return value;
        },
        backToStep1() {
            //this.progressClass = false;
            this.scope = "step-1";
        },
        continueToStep2() {
            let nextTab = this.nextAgendaTab;
            if (nextTab) {
                this.updateAgendaTab(nextTab);
                this.$refs.agendaTabs.setFocusTab(nextTab);

                var element = document.getElementById("sessionTabs");
                element.scrollIntoView({behavior: "smooth", block: "start"});
                return null;
            } else {
                //   this.progressClass = true;

                this.scope = "step-2";
                VueScrollTo.scrollTo("#sessionTabs", 1000, {offset: -160});
                //   this.processing = true;
            }
        },

        continueToStep3() {
            this.scope = "step-3";
            //   this.processing = true;
        },

        parseTracks(sessions) {
            let tracks = [];

            let keys = Object.keys(sessions);
            let track = "";
            let i = 0;
            let j = 0;

            for (i = 0; i < keys.length; i++) {
                for (j = 0; j < sessions[keys[i]].length; j++) {
                    track = sessions[keys[i]][j].track;
                    if (track) {
                        if (!tracks.includes(track)) {
                            tracks.push(track);
                        }
                    }
                }
            }

            if (tracks.length == 0) {
                return ["Sessions"];
            }

            tracks.sort();
            return tracks;
        },
        agendaColumns(sessions) {
            let tracks = [];

            let keys = Object.keys(sessions);
            let track = "";
            let i = 0;
            let j = 0;

            for (i = 0; i < keys.length; i++) {
                for (j = 0; j < sessions[keys[i]].length; j++) {
                    track = sessions[keys[i]][j].track;
                    if (track) {
                        if (!tracks.includes(track)) {
                            tracks.push(track);
                        }
                    }
                }
            }
    
            let agendaClass = "agenda" + " agenda--" + tracks.length.toString() + "-column";
            return agendaClass;
        },
        formatHour(day, hour) {
            let value = day + " " + hour;
            let time = this.parseDateString(value);

            if (time) {
                return time.format("h:mmA"); //changed this to output minutes b/c that's what the client wanted
            }

            return "";
        },
        formatSessionTime(session) {
            let temp = session.sessionDate + " " + session.sessionStartTime;
            let start = this.parseDateString(temp);

            temp = session.sessionDate + " " + session.sessionEndTime;

            let end = this.parseDateString(temp);

            return start.format("h:mmA") + "-" + end.format("h:mmA z");
        },
        formatDate(date) {
            let temp = this.parseDateString(date);

            return temp.format("MMM D");
        },
        checkboxId(session) {
            return session.sessionId;
        },
        filterTrackedSessions(columns, track) {
            let sessions = [];
            let i = 0;
            let session = "";

            for (i = 0; i < columns.length; i++) {
                session = columns[i];
                if (track == session.track) {
                    sessions.push(session);
                }
            }

            return sessions;
        },
        hasTracks(columns) {
            let i = 0;
            let session = "";

            for (i = 0; i < columns.length; i++) {
                session = columns[i];
                if (session.track) {
                    return true;
                }
            }

            return false;
        },
        spannedSessions(columns) {
            let values = [];
            let i = 0;
            let session = "";

            for (i = 0; i < columns.length; i++) {
                session = columns[i];
                if (!session.track) {
                    values.push(session);
                }
            }

            if (values.length == 0) {
                return null;
            }

            return values;
        },
        showAdditionalInfo(session) {
            if ("Meal Session" == session.sessionType) {
                return true;
            }

            return false;
        },
        showCheckbox(session) {
            if ("Networking Session" == session.sessionType) {
                return false;
            }
            return true;
        },
        updateAgendaTab(tab) {
            if (-1 === this.agendaTabsViewed.indexOf(tab)) {
                this.agendaTabsViewed.push(tab);
            }
        },
        completeRegistration() {
            this.error = null;
            this.success = null;


            // Form validation
            let errors = [];

            if ("" == this.user.firstName || null == this.user.firstName) {
                errors.push("First Name can not be blank");
            }
            if ("" == this.user.lastName || null == this.user.lastName) {
                errors.push("Last Name can not be blank");
            }
            if ("" == this.user.jobTitle || null == this.user.jobTitle) {
                errors.push("Title can not be blank");
            }
            if ("" == this.emailName || null == this.emailName) {
                errors.push("Email can not be blank");
            } else if (this.emailName.includes('@')) {
                errors.push("Email must be a valid format");
            }
            this.user.email = this.emailName + this.emailDomain;
            /*axios
                .get("/actions/auth/api/check-email", {
                        params: { email: this.user.email },
                    })
                .then((r) => {
                    if (r.data == 1) {
                        console.log("Email already registered");
                        errors.push("That email is already registered.");
                    }
                });*/
            if ("" == this.user.phone || null == this.user.phone) {
                errors.push("Phone can not be blank");
            }
            if (!(/\d/.test(this.user.phone))) {
                errors.push("Phone must contain a number");
            }

            if (errors.length) {
                this.error = errors.join("<br>");
                return false;
            }
            this.processing = true;

            let _self = this; // for setting error in closure scope

            //sanitize user info
            this.user.firstName = this.$sanitize(this.user.firstName);
            this.user.lastName = this.$sanitize(this.user.lastName);
            this.user.nickname = this.$sanitize(this.user.nickname);
            this.user.jobTitle = this.$sanitize(this.user.jobTitle);

            let data = {
                event_id: this.id,
                sessions: JSON.stringify(this.selectedSessions),
                additionalInfo: "",
                user: JSON.stringify(this.user),
                vendorShare: this.vendorShare,
            };
            axios.post("/actions/event-registrations/register", data, {
                headers: {"X-CSRF-Token": window.token},
            })
                .then((r) => {
                    if ("Success" == r.data.status) {
                        _self.success = r.data.message;
                        _self.continueToStep3();

                        axios.get("/actions/queue/run").then((r) => {
                            //console.log("response from queue/run");
                            //console.log(r);
                        });
                        return true;
                    } else if ("Error" == r.data.status) {
                        console.log(r)
                        _self.error = r.data.message;
                        _self.processing = false;
                        return false;
                    } else {
                        console.log(r)
                        if (r.data && r.data.message && r.data.message.includes('Address in mailbox given')) {
                            _self.error = "Invalid email address";
                        } else {
                            _self.error = JSON.stringify(r);
                        }

                    }
                })
                .catch((r) => {
                    _self.error = "Something went wrong";
                    console.log(r)
                    this.processing = false;
                });
            //unsanitize
            this.user.firstName = this.cleanAmp(this.user.firstName);
            this.user.lastName = this.cleanAmp(this.user.lastName);
            this.user.nickname = this.cleanAmp(this.user.nickname);
            this.user.jobTitle = this.cleanAmp(this.user.jobTitle);
        },

        loadEventDetails() {
            // Fetch event info from the eventregistrations API
            var _self = this;

            axios
                .get("/actions/event-registrations/register/fetch-event-info", {
                    params: {id: this.id},
                })
                .then((r) => {
                    if (r.data.validated) {
                        _self.event.title = r.data.title;
                        _self.event.startDate = r.data.startDate;
                        _self.event.endDate = r.data.endDate;
                        _self.event.location = r.data.location;
                        _self.event.timezone = r.data.eventTimezone;
                        _self.event.city = r.data.city;
                        _self.event.venue = r.data.venue;
                        _self.event.host = r.data.host;
                        _self.event.calendarUrl = r.data.calendarUrl;
                        _self.event.registrationId = r.data.registrationId;
                        _self.event.displayVendorShareCheckbox = r.data.displayVendorShareCheckbox;
                        _self.vendorShare = r.data.vendorShare;

                        if (Object.keys(r.data.sessions).length > 0) {
                            _self.sessions = r.data.sessions;

                            let keys = Object.keys(_self.agendaTabs);
                            _self.agendaTabsViewed.push(keys[0]);
                        } else {
                            _self.scope = "step-2";
                        }

                        if (_self.event.timezone && _self.event.location == "Virtual") {
                            moment.tz.setDefault(_self.user.timezone);
                        } else {
                            moment.tz.setDefault(_self.event.timezone);
                        }
                    } else {
                        _self.error = r.data.error;
                    }
                });
        },

        cleanAmp(str) {
            if (str) {
                return str.replaceAll('&amp;', '&');
            } else {
                return "";
            }
        },

        loadUser() {
            // Fetch user info from the eventregistrations API
            var _self = this;

            axios
                .get("/actions/event-registrations/register/fetch-user-info")
                .then((r) => {
                    _self.user.firstName = this.cleanAmp(r.data.firstName);
                    _self.user.lastName = this.cleanAmp(r.data.lastName);
                    _self.user.nickname = this.cleanAmp(r.data.nickname);
                    _self.user.jobTitle = this.cleanAmp(r.data.jobTitle);
                    _self.user.phone = r.data.phone;
                    _self.user.email = r.data.email;
                    _self.user.firm = r.data.acct;
                    if (r.data.timezone) {
                        _self.user.timezone = r.data.timezone;
                    } else {
                        _self.user.timezone = "America/New_York";
                    }

                    let temp = r.data.email.split("@");

                    _self.emailName = temp[0];
                    _self.emailDomain = "@" + temp[1];
                    this.loadEventDetails(); //chained here so that user information is populated before it runs
                });
        },
    },
    mounted() {
        this.loadUser();
    }
};
</script>